import { AudioLines, MessagesSquare, UserRoundSearch } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  AgentCore,
  ConnectionLines,
  GraphicCard,
  IllustrationFrame,
  StackedCard,
  VerifiedPill,
} from './shared'

/** Bar heights of the waveform, left to right; which voice each stretch belongs to is drawn, not random. */
const BARS = [
  10, 22, 34, 26, 40, 30, 18, 12, 28, 42, 36, 24, 14, 8, 20, 32, 44, 30, 16, 10,
] as const
/** Bars 0–7 and 14–19 are the first voice, 8–13 the second: the turn-taking of a conversation. */
const SECOND_VOICE = new Set([8, 9, 10, 11, 12, 13])

/** A recording with the one thing this feature does drawn on it: its two voices told apart. */
function Recording({ title, voices }: { title: string; voices: readonly [string, string] }) {
  return (
    <g>
      <StackedCard x={44} y={49} width={192} height={188} />
      <AudioLines x="60" y="64" width="20" height="20" className="text-primary-strong" />
      <text x="90" y="79" className="fill-foreground text-xs font-semibold">
        {title}
      </text>
      {/* the waveform: two voices, two tones */}
      {BARS.map((height, index) => (
        <rect
          key={index}
          x={60 + index * 8}
          y={140 - height / 2}
          width="4"
          height={height}
          rx="2"
          className={SECOND_VOICE.has(index) ? 'fill-muted-foreground/40' : 'fill-primary/70'}
        />
      ))}
      <rect x="60" y="176" width="58" height="18" rx="9" className="fill-primary/15" />
      <text
        x="89"
        y="188.5"
        textAnchor="middle"
        className="fill-primary-strong text-3xs font-medium"
      >
        {voices[0]}
      </text>
      <rect x="126" y="176" width="58" height="18" rx="9" className="fill-muted" />
      <text
        x="155"
        y="188.5"
        textAnchor="middle"
        className="fill-muted-foreground text-3xs font-medium"
      >
        {voices[1]}
      </text>
      {/* the transcript below: a line per turn, each in its speaker's tone */}
      <circle cx="64" cy="210" r="3" className="fill-primary/70" />
      <path
        d="M72 210H186"
        className="stroke-muted-foreground/30"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx="64" cy="224" r="3" className="fill-muted-foreground/40" />
      <path
        d="M72 224H150"
        className="stroke-muted-foreground/30"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </g>
  )
}

export function VoiceMatchingIllustration() {
  const { t } = useTranslation()
  const key = 'whatsNew.slides.voiceMatching.graphic'
  return (
    <IllustrationFrame label={t(`${key}.description`)}>
      <ConnectionLines d="M236 145H285 M351 145H386Q402 145 402 128V79H437 M402 128V177H437" />
      <Recording
        title={t(`${key}.recording`)}
        voices={[t(`${key}.voiceOne`), t(`${key}.voiceTwo`)]}
      />
      <AgentCore label={t(`${key}.processing`)} />
      <GraphicCard card={{ x: 437, y: 50, label: t(`${key}.transcript`), icon: MessagesSquare }} />
      <GraphicCard card={{ x: 437, y: 148, label: t(`${key}.match`), icon: UserRoundSearch }} />
      <circle cx="402" cy="128" r="4" className="fill-primary" />
      <VerifiedPill label={t(`${key}.confirmed`)} />
    </IllustrationFrame>
  )
}
